import {
	FREE_EDITION,
	STANDARD_EDITION,
	UNLICENSED,
} from '@jira/platform__application-editions/src/edition.tsx';
import { isFedRamp, isIsolatedCloud } from '@atlassian/atl-context';
import { useApiKeyManagementInfo } from '@atlassian/jira-eoc-api-key-management/src/controllers/index.tsx';
import { useOgTenantInfo } from '@atlassian/jira-eoc-tenant-info/src/controllers/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/utils/throw-when-no-data-and-error/index.tsx';
import { useNavigationGlobalResource } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-settings-global-common/src/controllers/navigation-global-resource/index.tsx';
import { hasItemInSidebar } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-settings-global-common/src/utils/has-item-in-sidebar/index.tsx';
import {
	SERVICE_DESK,
	SOFTWARE,
	CUSTOMER_SERVICE,
} from '@atlassian/jira-shared-types/src/application-key.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { RouteResourceError } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import type { Features } from '../../common/types.tsx';

type UseFeaturesReturn = {
	data: Features | null;
	loading: boolean;
	error: RouteResourceError | null;
};

export const useFeatures = withThrowOnError((): UseFeaturesReturn => {
	const {
		data: navigationGlobalResourceData,
		loading: navigationGlobalResourceLoading,
		error: navigationGlobalResourceError,
	} =
		// eslint-disable-next-line jira/react/no-usage-of-hooks-outside-components
		useNavigationGlobalResource();
	const {
		shouldUseJSM,
		shouldAccessUnlockOperations,
		isTransitioning,
		loading: ogTenantInfoLoading,
		error: ogTenantInfoError,
	} =
		// eslint-disable-next-line jira/react/no-usage-of-hooks-outside-components
		useOgTenantInfo();
	// eslint-disable-next-line jira/react/no-usage-of-hooks-outside-components
	const tenantContext = useTenantContext();
	// eslint-disable-next-line jira/react/no-usage-of-hooks-outside-components
	const appEditions = useAppEditions();
	// eslint-disable-next-line jira/react/no-usage-of-hooks-outside-components
	const { hasKeys, loading: apiKeyManagementLoading } = useApiKeyManagementInfo();

	if (fg('jira-delegated-custom-onboarding')) {
		// There are two considerations here to prevent flickering in the nav sidebar:
		// 1. When the EOC_TENANT resource (used by `useOgTenantInfo`) fails to fetch from the server,
		// a refetch is triggered *once* from the client. This means that if there is a server-side
		// ogTenantInfoError, we should wait for the client-side refetch to complete before returning
		// a non-null value for `data`.
		// 2. The EOC_CHECK_API_KEY resource (used by `useApiKeyManagementInfo`) is a browser-only
		// resource that is dependent on the EOC_TENANT resource. This means that we shouldn't wait
		// for it to load before rendering the nav sidebar.
		const ogTenantInfoServerError = __SERVER__ ? ogTenantInfoError : null;
		if (
			navigationGlobalResourceLoading ||
			!navigationGlobalResourceData ||
			ogTenantInfoLoading ||
			(ogTenantInfoServerError && fg('refresh_tenant_info_on_client_for_ssr_error'))
		) {
			return {
				data: null,
				loading: navigationGlobalResourceLoading || ogTenantInfoLoading,
				error: fg('refresh_tenant_info_on_client_for_ssr_error')
					? navigationGlobalResourceError || ogTenantInfoServerError
					: navigationGlobalResourceError,
			};
		}
	} else if (
		navigationGlobalResourceLoading ||
		!navigationGlobalResourceData ||
		ogTenantInfoLoading ||
		apiKeyManagementLoading
	) {
		return {
			data: null,
			loading: navigationGlobalResourceLoading || ogTenantInfoLoading || apiKeyManagementLoading,
			error: navigationGlobalResourceError,
		};
	}

	const hasJsmAboveStandard =
		!!tenantContext.licensedProducts[SERVICE_DESK] &&
		appEditions.serviceDesk !== FREE_EDITION &&
		appEditions.serviceDesk !== STANDARD_EDITION;

	const hasShiftFromOpsgenieToJira = shouldAccessUnlockOperations;
	const hideFeaturesIsolatedCloud =
		isIsolatedCloud() && fg('hide_chatops_component_in_isolated_cloud');
	const hasChatOpsToolAccess =
		!shouldUseJSM &&
		!!tenantContext.licensedProducts[SERVICE_DESK] &&
		!isFedRamp() &&
		!hideFeaturesIsolatedCloud;

	return {
		data: {
			hasApiKeyManagementAccess:
				isTransitioning &&
				shouldUseJSM &&
				hasJsmAboveStandard &&
				tenantContext.isSiteAdmin &&
				hasKeys,
			hasApplicationAccess: hasItemInSidebar(
				navigationGlobalResourceData,
				'application_access_browser',
			),
			hasApplicationLinksIntegration: hasItemInSidebar(
				navigationGlobalResourceData,
				'configure-application-links',
			),
			hasAuditLogsAccess: shouldUseJSM && !!tenantContext.licensedProducts[SERVICE_DESK],
			hasChatOpsToolAccess,
			hasCompassPageIntegration:
				tenantContext.isSiteAdmin && !!tenantContext.licensedProducts[SOFTWARE],
			hasConfluencePageIntegration: tenantContext.isAdmin,
			hasCustomerServiceSection:
				tenantContext.isAdmin &&
				!!tenantContext.licensedProducts[CUSTOMER_SERVICE] &&
				fg('jcs_master_flag'),
			hasDvcsAccountsIntegration: hasItemInSidebar(
				navigationGlobalResourceData,
				'bitbucket_bulk_repo',
			),
			hasGlobalIncomingCallAccess: shouldUseJSM && hasJsmAboveStandard,
			hasGlobalIntegrationAccess: shouldUseJSM && hasJsmAboveStandard,
			hasGlobalSyncAccess: shouldUseJSM && hasJsmAboveStandard,
			hasIntegrationsSection: hasItemInSidebar(
				navigationGlobalResourceData,
				'admin_applications_integrations_section',
			),
			hasMaintenanceAccess: shouldUseJSM && !!tenantContext.licensedProducts[SERVICE_DESK],
			hasShiftFromOpsgenieToJira,
			hasOperationsSection:
				tenantContext.isAdmin &&
				Boolean(tenantContext.licensedProducts[SERVICE_DESK]) &&
				(hasShiftFromOpsgenieToJira || hasChatOpsToolAccess),
			hasOrganizationsListAccess:
				!!tenantContext.licensedProducts[SERVICE_DESK] && tenantContext.isAdmin,
			hasPlansSection: hasItemInSidebar(navigationGlobalResourceData, 'jpo-ws-main-admin'),
			hasPoliciesAccess: shouldUseJSM && hasJsmAboveStandard,
			hasProductDiscoverySection:
				tenantContext.isAdmin && appEditions.productDiscovery !== UNLICENSED,
			hasRoleBasedNotificationsAccess: shouldUseJSM && tenantContext.isAdmin && hasJsmAboveStandard,
			hasServiceManagementAuthenticationSettings:
				tenantContext.isAdmin && appEditions.serviceDesk !== UNLICENSED,
			hasServiceManagementConfig: hasItemInSidebar(
				navigationGlobalResourceData,
				'sd-configuration-link',
			),
			hasServiceManagementCustomerAccess:
				tenantContext.isAdmin && appEditions.serviceDesk !== UNLICENSED,
			hasServiceManagementEmailRequests: hasItemInSidebar(
				navigationGlobalResourceData,
				'sd-admin-mail-info-link',
			),
			hasServiceManagementIncidentManagementTools: hasItemInSidebar(
				navigationGlobalResourceData,
				'sd-incident-management-tools-link',
			),
			hasServiceManagementKbPermissionsAccess: tenantContext.isAdmin,
			hasServiceManagementSection: hasItemInSidebar(
				navigationGlobalResourceData,
				'sd-plugin-admin-section',
			),
			hasSoftwareConfig: tenantContext.isAdmin,
			hasSoftwareSection: tenantContext.isAdmin,
			hasStatusPageIntegration:
				!!tenantContext.atlassianAccountId &&
				hasItemInSidebar(navigationGlobalResourceData, 'jsd-status-page__admin-page'),
		},
		loading: navigationGlobalResourceLoading,
		error: navigationGlobalResourceError,
	};
});
